import type { SpanExporter, ReadableSpan } from '@opentelemetry/sdk-trace-base';
import { ExportResultCode } from '@opentelemetry/core';
import type { ExportResult } from '@opentelemetry/core';
import type { AttributeValue } from '@opentelemetry/api';
export const LOCAL_OTLP_ENDPOINT = '/otlp/v1/traces';

// Browser keepalive requests are capped at 64KB; use 60KB to leave headroom.
export const MAX_KEEPALIVE_BYTES = 60 * 1024;

export interface HttpClient {
  post(url: string, options: { data: unknown }): Promise<unknown>;
}

function hrTimeToNanoString(hrTime: [number, number]): string {
  return String(BigInt(hrTime[0]) * 1_000_000_000n + BigInt(hrTime[1]));
}

function toOtlpAttributeValue(value: AttributeValue): object {
  if (typeof value === 'string') {
    return { stringValue: value };
  }
  if (typeof value === 'boolean') {
    return { boolValue: value };
  }
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? { intValue: String(value) }
      : { doubleValue: value };
  }
  if (typeof value === 'bigint') {
    return { intValue: String(value) };
  }
  if (Array.isArray(value)) {
    return {
      arrayValue: {
        values: value.map((v) => ({
          value: toOtlpAttributeValue(v as AttributeValue),
        })),
      },
    };
  }
  return { stringValue: String(value) };
}

function toOtlpAttributes(
  attributes: Record<string, AttributeValue | undefined>
): Array<{ key: string; value: object }> {
  return Object.entries(attributes)
    .filter(([, v]) => v !== undefined)
    .map(([key, value]) => ({
      key,
      value: toOtlpAttributeValue(value as AttributeValue),
    }));
}

export class OtlpExporter implements SpanExporter {
  private readonly _endpoint: string;
  private readonly _httpClient: HttpClient | null;

  constructor(
    endpoint: string = LOCAL_OTLP_ENDPOINT,
    { httpClient }: { httpClient?: HttpClient } = {}
  ) {
    this._endpoint = endpoint;
    this._httpClient = httpClient ?? null;
  }

  export(
    spans: ReadableSpan[],
    resultCallback: (result: ExportResult) => void
  ): void {
    const otlpSpans = spans.map((s) => this._toOtlpSpan(s));

    if (
      !this._httpClient &&
      spans.length > 1 &&
      new TextEncoder().encode(JSON.stringify(otlpSpans)).byteLength >
        MAX_KEEPALIVE_BYTES
    ) {
      const mid = Math.floor(spans.length / 2);
      let pending = 2;
      let failed: ExportResult | null = null;

      const onChunkResult = (result: ExportResult) => {
        if (result.code === ExportResultCode.FAILED) {
          failed = result;
        }
        if (--pending === 0) {
          resultCallback(failed ?? { code: ExportResultCode.SUCCESS });
        }
      };

      this.export(spans.slice(0, mid), onChunkResult);
      this.export(spans.slice(mid), onChunkResult);
      return;
    }

    const resourceSpans = this._groupByResource(spans, otlpSpans);
    const body = { resourceSpans };

    const request = this._httpClient
      ? this._httpClient.post(this._endpoint, { data: body })
      : fetch(this._endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
        }).then((response) => {
          if (!response.ok) {
            throw new Error(`Export failed with status ${response.status}`);
          }
        });

    request
      .then(() => resultCallback({ code: ExportResultCode.SUCCESS }))
      .catch((err: unknown) =>
        resultCallback({
          code: ExportResultCode.FAILED,
          error: err instanceof Error ? err : new Error(String(err)),
        })
      );
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  private _groupByResource(
    spans: ReadableSpan[],
    otlpSpans: object[]
  ): object[] {
    const resourceMap = new Map<
      object,
      Array<{ span: ReadableSpan; otlp: object }>
    >();

    for (let i = 0; i < spans.length; i++) {
      const resource = spans[i].resource;
      if (!resourceMap.has(resource)) {
        resourceMap.set(resource, []);
      }
      resourceMap.get(resource)!.push({ span: spans[i], otlp: otlpSpans[i] });
    }

    return Array.from(resourceMap.entries()).map(([resource, pairs]) => ({
      resource: {
        attributes: toOtlpAttributes(
          (
            resource as {
              attributes: Record<string, AttributeValue | undefined>;
            }
          ).attributes
        ),
      },
      scopeSpans: this._groupByScope(pairs),
    }));
  }

  private _groupByScope(
    pairs: Array<{ span: ReadableSpan; otlp: object }>
  ): object[] {
    const scopeMap = new Map<
      string,
      Array<{ span: ReadableSpan; otlp: object }>
    >();

    for (const pair of pairs) {
      const key = `${pair.span.instrumentationScope.name}@${
        pair.span.instrumentationScope.version ?? ''
      }`;
      if (!scopeMap.has(key)) {
        scopeMap.set(key, []);
      }
      scopeMap.get(key)!.push(pair);
    }

    return Array.from(scopeMap.entries()).map(([, scopePairs]) => {
      const scope = scopePairs[0].span.instrumentationScope;
      return {
        scope: { name: scope.name, version: scope.version ?? '' },
        spans: scopePairs.map(({ otlp }) => otlp),
      };
    });
  }

  private _toOtlpSpan(span: ReadableSpan): object {
    const ctx = span.spanContext();
    return {
      traceId: ctx.traceId,
      spanId: ctx.spanId,
      parentSpanId: span.parentSpanContext?.spanId,
      name: span.name,
      kind: span.kind + 1,
      startTimeUnixNano: hrTimeToNanoString(span.startTime),
      endTimeUnixNano: hrTimeToNanoString(span.endTime),
      attributes: toOtlpAttributes(
        span.attributes as Record<string, AttributeValue | undefined>
      ),
      status: {
        code: span.status.code,
        message: span.status.message,
      },
      droppedAttributesCount: span.droppedAttributesCount,
      droppedEventsCount: span.droppedEventsCount,
      droppedLinksCount: span.droppedLinksCount,
    };
  }
}
